<script lang="ts">
	import type { InputControl } from '$lib/components/atoms/input/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { ClassValue, FullAutoFill } from 'svelte/elements';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Icon from '$lib/components/icons/Icon.svelte';
	import * as m from '$paraglide/messages';
	import { onDestroy } from 'svelte';

	interface Props {
		id: string;
		value?: string;
		ariaLabel: string;
		disabled?: boolean;
		placeholder?: string;
		autocomplete?: FullAutoFill;
		icon?: IconName;
		controls?: InputControl[];
		label?: string;
		showCloseButton?: boolean;
		iconClasses?: ClassValue;
		active?: boolean;
		error?: boolean;
		class?: ClassValue;
		oninput?: (event: Event) => void;
		onchange?: (event: Event) => void;
		onkeydown?: (event: KeyboardEvent) => void;
		onclick?: (event: Event) => void;
		onActivate?: () => void;
		onDeactivate?: () => void;
	}

	let {
		id,
		value = $bindable(''),
		ariaLabel,
		disabled = false,
		placeholder,
		autocomplete = 'off',
		icon,
		controls,
		label,
		showCloseButton = true,
		iconClasses,
		active = $bindable(false),
		error = false,
		class: classes,
		oninput,
		onchange,
		onkeydown,
		onclick,
		onActivate,
		onDeactivate,
	}: Props = $props();

	let input: HTMLInputElement;

	export function blur() {
		input.blur();
	}

	let isClickFromClearButton = false;

	function clear() {
		value = '';
		if (input) {
			input.value = '';
			input.dispatchEvent(new Event('change', { bubbles: true }));
			input.focus();
		}
	}

	let isLabelHovered = $state(false);
	let isLabelHoveringTimer: NodeJS.Timeout | undefined;

	function onClearMouseDown() {
		isClickFromClearButton = true;

		setTimeout(() => {
			isClickFromClearButton = false;
		}, 250);
	}

	function onblur() {
		isLabelHovered = false;

		if (active) {
			if (isClickFromClearButton) {
				isClickFromClearButton = false;
				input?.focus(); // inside container, clicked on a button, refocus input
			} else {
				active = false;
				onDeactivate?.();
			}
		}
	}

	let timeoutId: ReturnType<typeof setTimeout>;

	function onfocus() {
		timeoutId = setTimeout(() => {
			// In IOS, the keyboard pushes the page up, but the page is not scrolled up
			// This is a workaround to scroll the page up when the input is focused
			// We can afford to do so because our root html element is set to overflow hidden
			window.scroll(0, 0);
		}, 500);

		if (!active) {
			active = true;
			onActivate?.();
		}
	}

	onDestroy(() => {
		clearTimeout(timeoutId);
	});

	function onMouseEnter() {
		if (!disabled) {
			// Delay the showClearX appearance by delaying isLabelHovered.
			// As the mouseEnter is also triggered in mobile by a tap
			// it avoids triggering the click event on the clear button.
			isLabelHoveringTimer = setTimeout(() => {
				isLabelHoveringTimer = undefined;
				isLabelHovered = true;
			}, 50);
		}
	}

	function onMouseLeave() {
		if (!disabled) {
			if (isLabelHoveringTimer) {
				clearTimeout(isLabelHoveringTimer);
				isLabelHoveringTimer = undefined;
			}
			isLabelHovered = false;
		}
	}

	const labelStateClasses = $derived.by(() => {
		const className = [];

		if (disabled) {
			className.push('element-disabled bg-neutral-soft text-neutral');
		} else {
			className.push('bg-neutral-weakest text-coloredneutral');
			if (active) {
				className.push('shadow-focus border-focus border-1');
			} else {
				className.push('hover:shadow-subtle');
			}
		}

		return className;
	});

	const showClearX = $derived(showCloseButton
		? (isLabelHovered || active) && !disabled && value?.length > 0
		: false);
</script>

<label for={id} class="text-sm">
	{label}
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<span
		class={[
			'input-container flex items-center p-2 w-full text-5 gap-1 rd-2 relative b-solid b-0.5 border-coloredneutral-weakest',
			!!label && 'mt-1',
			labelStateClasses,
			error && 'border-error border-1',
			classes,
		]}
		onmouseleave={onMouseLeave}
		onmouseenter={onMouseEnter}
	>
		{#if icon}
			<Icon {icon} class="w-5 {iconClasses}" tag="span" />
		{/if}
		<input
			type="text"
			{id}
			class={['input min-w-0 text-md text-coloredneutral b-0 flex-grow bg-transparent placeholder:text-neutral disabled:text-neutral', error && 'text-error']}
			{placeholder}
			aria-label={ariaLabel}
			{disabled}
			{autocomplete}
			bind:this={input}
			{oninput}
			{onchange}
			{onkeydown}
			{onfocus}
			{onblur}
			bind:value
			{onclick}
		/>
		<!-- add more forwarded events above as needed, but not focus, we emit activate/deactivate for that -->

		{#if showClearX}
			<Button
				variant="ghost"
				icon="close"
				ariaLabel={m.input__clear()}
				{disabled}
				onclick={clear}
				onpointerdown={event => event.preventDefault()}
				onmousedown={onClearMouseDown}
			/>
		{/if}
		{#if controls && controls.length}
			<span class="flex">
				<!-- eslint-disable-next-line svelte/require-each-key -->
				{#each controls ?? [] as control}
					<Button
						variant="ghost"
						icon={control.icon}
						ariaLabel={control.ariaLabel}
						class="{control.class} p-2"
						{disabled}
						onclick={control.click}
						onpointerdown={event => event.preventDefault()}
					/>
				{/each}
			</span>
		{/if}
	</span>
</label>

<style>
	.input-container {
		height: 2.75rem;
	}
	.input {
		height: 1.75rem;
		&:focus-visible {
			outline: none;
		}
	}

</style>
